/**
 * Port of lib/candidate_audit.py.
 *
 * Per-candidate explainability — say WHY a candidate time scored as it did, and how much to
 * trust it.
 *
 * The honesty risk this closes: a single number ("87.3 at 05:48:03") reads as certainty. This
 * module turns each candidate into a plain-language audit — is the time led by dated events, by
 * appearance, or genuinely under-determined; which event and which classical component moved the
 * score the most. It reads only fields already on the candidate record (event_fit_score,
 * rising_sign_fit_score, kp_details), so it adds no computation to the scan.
 */

import { KP_COMPONENT_WEIGHTS } from "./kp_scoring";
import { MIN_EVENTS_FOR_CONFIDENCE } from "./rising_sign_confidence";
import { pythonRound } from "./pyutil";

// The KP component a result can be attributed to, in human terms. Every entry must carry a
// per-event `score` in kp_details (house_ashtakavarga does NOT — kp_scoring stores only its
// `factor` — so it is deliberately absent here).
export const COMPONENT_LABELS: Record<string, string> = {
  dasha: "Vimshottari dasha timing",
  divisional: "divisional-chart (varga) fit",
  divisional_fine: "D60 fine refinement",
  cusp_sublord: "cuspal sub-lord",
  asc_sublord: "Ascendant sub-lord",
  ruling_planets: "ruling planets",
  transit: "transit (gochar) confirmation",
  yoga: "yoga formation/activation",
  special_lagna_ghati: "Ghati-Lagna confirmation",
  special_lagna_hora: "Hora-Lagna confirmation",
  special_lagna_bhava: "Bhava-Lagna confirmation",
};
// Components that carry a per-event 0-100 score we can weight. (net_strength/houses are context.)
const _SCORED_COMPONENTS = Object.keys(COMPONENT_LABELS);

/** Python float repr for a 1-decimal rounded number (90 -> "90.0", 52.5 -> "52.5"). */
function _float_repr(x: number): string {
  return Number.isInteger(x) ? x.toFixed(1) : String(x);
}

/**
 * The single (event, KP component) pair that contributes the most weighted evidence across
 * all dated events — i.e. what is really holding this time up. None if there are no scored
 * components.
 */
export function dominant_event_and_component(
  kp_details: Array<Record<string, any>>,
): Record<string, any> | null {
  let best: Record<string, any> | null = null;
  let best_weighted = Number.NEGATIVE_INFINITY;
  for (const detail of kp_details || []) {
    // Python: float(detail.get("effective_weight") or 0.0) — falsy (None/0/"") collapses to 0.
    const ew = detail["effective_weight"];
    const event_weight = ew ? Number(ew) : 0.0;
    const components = detail["components"] || {};
    for (const key of _SCORED_COMPONENTS) {
      const comp = components[key];
      if (typeof comp !== "object" || comp === null || Array.isArray(comp) || comp["score"] == null) {
        continue;
      }
      // Weight = how far the component leans from neutral × its engine weight × the event's
      // weight. A component sitting at ~50 (neutral) is not "driving" anything.
      const lean = Math.abs(Number(comp["score"]) - 50.0);
      const weighted = lean * Number(KP_COMPONENT_WEIGHTS[key] ?? 0.0) * event_weight;
      if (weighted > best_weighted) {
        best_weighted = weighted;
        best = {
          event: detail["label"] || detail["category"] || detail["date"],
          date: detail["date"],
          component: key,
          component_label: COMPONENT_LABELS[key],
          component_score: pythonRound(Number(comp["score"]), 1),
        };
      }
    }
  }
  return best && best_weighted > 0 ? best : null;
}

/**
 * Plain-language audit of one candidate record.
 *
 * Returns {lead, evidence, reason, driver, underdetermined} where `lead` is one of
 * 'event-led', 'appearance-led', 'mixed', or 'no-evidence'. `underdetermined` is True when the
 * dated evidence is too thin to trust the rising sign (echoes the confidence layer's gate), so
 * the UI can warn rather than present the second as settled.
 */
export function audit_candidate(record: Record<string, any>, event_count: number): Record<string, any> {
  const event_fit = record["event_fit_score"] ?? null;
  const rs_fit = record["rising_sign_fit_score"] ?? null;
  const driver = dominant_event_and_component(record["kp_details"] || []);
  const underdetermined = event_count < MIN_EVENTS_FOR_CONFIDENCE;

  if (event_fit === null && rs_fit === null) {
    return {
      lead: "no-evidence",
      evidence: "none",
      reason: "No dated events and no appearance/temperament answers — nothing ranks this time.",
      driver: null,
      underdetermined: true,
    };
  }
  if (event_fit === null) {
    return {
      lead: "appearance-led",
      evidence: "appearance only",
      reason:
        "No dated events: the rising sign is chosen from appearance/temperament alone. " +
        "This shortlists a sign, it cannot fix the exact time — add dated events to time it.",
      driver: driver,
      underdetermined: true,
    };
  }
  let lead: string;
  let reason: string;
  if (rs_fit === null) {
    lead = "event-led";
    reason = "Ranked by how the dated events time out (KP dasha + significators).";
  } else if (event_count <= 1) {
    lead = "appearance-led";
    reason =
      "Only one dated event, which cannot fix the rising sign on its own, so observed " +
      "appearance carries most of the weight here.";
  } else if (underdetermined) {
    lead = "mixed";
    reason =
      `Events lead but ${event_count} of them under-determine the rising sign, so appearance ` +
      "still meaningfully shifts the result — treat the sign as provisional.";
  } else {
    lead = "event-led";
    reason = "Dated events across enough evidence lead the ranking; appearance only breaks ties.";
  }

  if (underdetermined && lead !== "appearance-led") {
    reason += ` Fewer than ${MIN_EVENTS_FOR_CONFIDENCE} well-dated events — the rising sign is not yet certain.`;
  }

  return {
    lead: lead,
    evidence: underdetermined ? "thin" : "adequate",
    reason: reason,
    driver: driver,
    underdetermined: underdetermined,
  };
}

/** One-line human summary for compact display. */
export function audit_sentence(record: Record<string, any>, event_count: number): string {
  const audit = audit_candidate(record, event_count);
  const parts = [`**${audit["lead"]}** — ${audit["reason"]}`];
  const driver = audit["driver"];
  if (driver) {
    parts.push(
      `Strongest single factor: ${driver["component_label"]} of "${driver["event"]}" ` +
        `(score ${_float_repr(driver["component_score"])}).`,
    );
  }
  return parts.join(" ");
}
